import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const SEV = {
  high:   { label: 'Alta',   bg: 'var(--red-dim)', color: '#B0291D', border: '#F5C0BC', dot: '🔴' },
  medium: { label: 'Media',  bg: '#FFF8E1', color: '#8C6B00', border: '#FFD54F', dot: '🟡' },
  low:    { label: 'Baja',   bg: '#F0F4F8', color: '#4A5568', border: '#CBD5E0', dot: '🔵' },
};

// Paleta para el estado "en revisión" (marcada pero sin cerrar)
const REVIEW = { bg: 'var(--accent-dim)', border: 'var(--accent-dim)', color: 'var(--accent)' };

const TYPE_LABELS = {
  remote_access_tool:       'Acceso remoto',
  software_outside_hours:   'Software fuera de horario',
  security_software_removed:'Seguridad removida',
  firewall_disabled:        'Firewall desactivado',
  antivirus_disabled:       'Antivirus desactivado',
  bitlocker_disabled:       'BitLocker desactivado',
  new_local_admin:          'Nuevo admin local',
  disk_space_drop:          'Caída de espacio en disco',
  new_wifi_profile:         'Nuevo perfil WiFi',
};

function SevBadge({ severity }) {
  const s = SEV[severity] || SEV.low;
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}


// Tiempo relativo corto para "última vez". Un "hace 3 días" comunica el abandono de una
// alerta mejor que una fecha, que obliga al lector a hacer la cuenta.
function diasDesde(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return 'minutos';
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} día${d === 1 ? '' : 's'}`;
}
export default function Anomalies() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('open'); // open | all
  const [severity, setSeverity] = useState('');
  const [acting, setActing]     = useState({});

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('resolved', filter === 'open' ? 'false' : 'all');
    if (severity) params.set('severity', severity);
    fetch(`${API_URL}/anomalies?${params}`, { headers })
      .then(r => r.json())
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [filter, severity]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // "Revisar" = marcar EN REVISIÓN (no la cierra). Toggle: vuelve a click la desmarca.
  const toggleReview = async (id, current) => {
    setActing(p => ({ ...p, [id]: 'review' }));
    await fetch(`${API_URL}/anomalies/${id}/review`, { method: 'PATCH', headers, body: JSON.stringify({ reviewing: !current }) });
    setActing(p => ({ ...p, [id]: null }));
    load();
  };

  const markFalsePositive = async (id) => {
    setActing(p => ({ ...p, [id]: 'fp' }));
    await fetch(`${API_URL}/anomalies/${id}/false-positive`, { method: 'PATCH', headers, body: JSON.stringify({}) });
    setActing(p => ({ ...p, [id]: null }));
    load();
  };

  const createIncident = async (id) => {
    setActing(p => ({ ...p, [id]: 'incident' }));
    const r = await fetch(`${API_URL}/anomalies/${id}/incident`, { method: 'POST', headers, body: JSON.stringify({}) });
    const d = await r.json();
    setActing(p => ({ ...p, [id]: null }));
    if (d.ok) load();
  };

  const runDetection = async () => {
    await fetch(`${API_URL}/anomalies/run`, { method: 'POST', headers });
    setTimeout(load, 2000);
  };

  const summary = data?.summary || {};
  const anomalies = data?.anomalies || [];

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)' }}>Anomalías detectadas</div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
            Cambios sospechosos detectados comparando scans consecutivos de cada equipo
          </div>
        </div>
        <button className="btn btn-secondary" onClick={runDetection} style={{ fontSize: 12 }}>
          ▶ Ejecutar ahora
        </button>
      </div>

      {/* Stat strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Abiertas',  value: summary.total_open || 0, color: 'var(--gray-700)' },
          { label: 'Alta',      value: summary.high   || 0,    color: '#B0291D' },
          { label: 'Media',     value: summary.medium || 0,    color: '#8C6B00' },
          { label: 'Baja',      value: summary.low    || 0,    color: '#4A5568' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: 'var(--gray-0)', border: '1px solid var(--gray-200)',
            borderRadius: 8, padding: '14px 18px' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {[{ k: 'open', l: 'Abiertas' }, { k: 'all', l: 'Todas' }].map(f => (
          <button key={f.k} className={`btn ${filter === f.k ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter(f.k)} style={{ fontSize: 12 }}>{f.l}</button>
        ))}
        <select value={severity} onChange={e => setSeverity(e.target.value)}
          style={{ padding: '5px 10px', border: '1px solid var(--gray-300)', borderRadius: 6,
            fontSize: 12, background: 'var(--gray-0)', color: 'var(--gray-900)' }}>
          <option value="">Todas las severidades</option>
          <option value="high">Alta</option>
          <option value="medium">Media</option>
          <option value="low">Baja</option>
        </select>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Cargando...</div>
      ) : anomalies.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--gray-400)', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
          Sin anomalías {filter === 'open' ? 'abiertas' : ''} 🎉
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {anomalies.map(a => {
            const s = SEV[a.severity] || SEV.low;
            const isActing = acting[a.id];
            const inReview = a.reviewing && !a.resolved;
            // Color: resuelta → gris · en revisión → índigo · abierta → color por severidad
            const cardBg = a.resolved ? 'var(--gray-50)' : inReview ? REVIEW.bg : s.bg;
            const cardBorder = a.resolved ? 'var(--gray-200)' : inReview ? REVIEW.border : s.border;
            return (
              <div key={a.id} style={{ border: `1px solid ${cardBorder}`,
                borderLeft: inReview ? `4px solid ${REVIEW.color}` : `1px solid ${cardBorder}`,
                borderRadius: 8, padding: '14px 16px', background: cardBg,
                opacity: a.resolved ? 0.7 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: a.resolved ? 'var(--gray-500)' : s.color }}>
                        {s.dot} {a.title}
                      </span>
                      <SevBadge severity={a.severity} />
                      <span style={{ fontSize: 11, background: 'var(--gray-100)', color: 'var(--gray-600)',
                        padding: '2px 6px', borderRadius: 4 }}>
                        {TYPE_LABELS[a.type] || a.type}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--gray-600)', marginBottom: 6 }}>
                      {a.description}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span>💻 {a.asset_id
                        ? <Link to={`/assets/${a.asset_id}`} style={{ color: 'var(--blue)', textDecoration: 'none', fontWeight: 600 }}
                            title="Ver equipo en el inventario">{a.hostname}</Link>
                        : (a.hostname || '—')}</span>
                      <span>👤 {a.user_name || '—'}</span>
                      <span title="Primera vez que se detectó">🕐 {new Date(a.detected_at).toLocaleString('es-AR')}</span>
                      {/* Una condición que persiste es UNA alerta que se repite. Mostrar
                          cuántas veces y desde cuándo dice más que N filas iguales: revela
                          que hace semanas que nadie la mira. */}
                      {a.occurrences > 1 && (
                        <span style={{ color: 'var(--gray-600)', fontWeight: 600 }}
                          title={`Última vez: ${new Date(a.last_detected_at).toLocaleString('es-AR')}`}>
                          ↻ visto {a.occurrences} veces
                          {a.last_detected_at && ` · última hace ${diasDesde(a.last_detected_at)}`}
                        </span>
                      )}
                      {inReview && (
                        <span style={{ background: REVIEW.bg, color: REVIEW.color, border: `1px solid ${REVIEW.border}`,
                          padding: '1px 7px', borderRadius: 4, fontWeight: 600 }}>👁 En revisión</span>
                      )}
                      {a.resolved && a.false_positive && (
                        <span style={{ background: 'var(--gray-100)', color: 'var(--gray-600)', border: '1px solid var(--gray-300)',
                          padding: '1px 7px', borderRadius: 4, fontWeight: 600 }}>Falso positivo</span>
                      )}
                      {a.resolved && !a.false_positive && a.resolution && <span>✓ {a.resolution}</span>}
                    </div>
                  </div>
                  {!a.resolved && (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button className="btn btn-secondary"
                        disabled={!!isActing}
                        onClick={() => toggleReview(a.id, inReview)}
                        title={inReview ? 'Quitar de revisión' : 'Marcar en revisión'}
                        style={{ fontSize: 11, padding: '4px 10px',
                          ...(inReview ? { background: REVIEW.color, color: 'var(--surface)', borderColor: REVIEW.color } : {}) }}>
                        {isActing === 'review' ? '...' : inReview ? '👁 En revisión' : '👁 Revisar'}
                      </button>
                      <button className="btn btn-secondary"
                        disabled={!!isActing}
                        onClick={() => markFalsePositive(a.id)}
                        title="Marcar como falso positivo"
                        style={{ fontSize: 11, padding: '4px 10px' }}>
                        {isActing === 'fp' ? '...' : '⊘ Falso positivo'}
                      </button>
                      <button className="btn btn-primary"
                        disabled={!!isActing}
                        onClick={() => createIncident(a.id)}
                        style={{ fontSize: 11, padding: '4px 10px' }}>
                        {isActing === 'incident' ? '...' : '→ Incidente'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
